/**
 * Coquille de la page projet : en-tête (clé, nom, statut, période, avancement) + onglets de vue.
 *
 * L'onglet actif est porté par l'URL (`?vue=`) pour rester partageable/rechargeable.
 * Le panneau de détail d'une tâche (`TaskDrawer`) vit ici : n'importe quelle vue peut
 * l'ouvrir via `onOpenTask`, et sa fermeture déclenche un rechargement des vues (`reloadKey`).
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roleAtLeast } from '@exiplan/shared-types';
import { api } from '../lib/api.js';
import { useAsync } from '../hooks/useAsync.js';
import { useMediaQuery } from '../hooks/useMediaQuery.js';
import { appAlert } from '../dialogs/service.js';
import {
  Eyebrow,
  LoadError,
  PageHeader,
  PrimaryButton,
  Progress,
  StatusTag,
} from '../components/common.jsx';
import { Chevron, Deroulant } from '../components/common/Deroulant.jsx';
import { Modal } from '../components/Modal.jsx';
import { ShareProjectDialog } from '../dialogs/ShareProjectDialog.jsx';
import { TaskForm } from '../components/TaskForm.jsx';
import { TaskDrawer } from '../components/TaskDrawer.jsx';
import { DashboardView } from '../views/DashboardView.jsx';
import { KanbanView } from '../views/KanbanView.jsx';
import { GanttView } from '../views/GanttView.jsx';
import { PlanningView } from '../views/PlanningView.jsx';
import { ListView } from '../views/ListView.jsx';
import { SettingsView } from '../views/SettingsView.jsx';
import { TransferPanel } from '../views/TransferPanel.jsx';
import {
  BTN_GHOST,
  MENU_CHECK_OFF,
  MENU_CHECK_ON,
  MENU_RADIO,
  TAB_ACTIVE,
  TAB_IDLE,
} from '../ui/theme.js';

/** Sous 720 px, la rangée d'onglets déborde (coquille.md §2.3/§6.4) : elle se regroupe dans
 * un menu déroulant « Plus », seul l'onglet actif reste une destination directe. */
const SEUIL_ONGLETS_COMPACT = '(max-width: 719px)';

const TAB_KEYS = [
  { key: 'tableau-de-bord', labelKey: 'project.tabs.dashboard' },
  { key: 'kanban', labelKey: 'project.tabs.kanban' },
  { key: 'planning', labelKey: 'project.tabs.planning' },
  { key: 'gantt', labelKey: 'project.tabs.gantt' },
  { key: 'liste', labelKey: 'project.tabs.list' },
  { key: 'transfert', labelKey: 'project.tabs.transfer' },
  { key: 'reglages', labelKey: 'project.tabs.settings' },
];

export function ProjectPage() {
  const { t } = useTranslation();
  const TABS = TAB_KEYS.map((tab) => ({ key: tab.key, label: t(tab.labelKey) }));
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTaskId, setSelectedTaskId] = useState(() => searchParams.get('tache'));
  const [reloadKey, setReloadKey] = useState(0);
  /**
   * Création de tâche. `null` = boîte fermée ; sinon les présélections de la boîte. Un `null`
   * distinct d'un objet aux deux champs vides est nécessaire : ouvrir sans présélection est
   * une ouverture légitime, déclenchée par le bouton « nouvelle tâche » de l'en-tête.
   */
  const [creating, setCreating] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sharingOpen, setSharingOpen] = useState(false);

  const onglettesCompacts = useMediaQuery(SEUIL_ONGLETS_COMPACT);

  const activeTab = searchParams.get('vue') || 'kanban';
  const setActiveTab = useCallback(
    (key) => {
      const next = new URLSearchParams(searchParams);
      next.set('vue', key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const {
    data: project,
    error: projectError,
    reload: reloadProject,
  } = useAsync(
    (signal) => api.get(`/projects/${projectId}`, { signal }).then((r) => r.data),
    [projectId],
  );
  const { data: stats } = useAsync(
    (signal) => api.get(`/projects/${projectId}/stats`, { signal }).then((r) => r.data),
    [projectId, reloadKey],
  );

  const openTask = useCallback((taskId) => setSelectedTaskId(taskId), []);
  // `?tache=` n'est écrit que par un lien externe (vue d'ensemble) : `openTask` n'y touche
  // jamais, sinon toute ouverture depuis une vue interne polluerait l'URL. La fermeture, elle,
  // retire le paramètre s'il est présent — sinon revenir à cette page depuis l'historique
  // rouvrirait le panneau qu'on venait de fermer.
  const closeTask = useCallback(() => {
    setSelectedTaskId(null);
    if (searchParams.has('tache')) {
      const next = new URLSearchParams(searchParams);
      next.delete('tache');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const onTaskChanged = useCallback(() => setReloadKey((k) => k + 1), []);

  const openCreateTask = useCallback(
    (columnId = '') => setCreating({ columnId: columnId ?? '', parentId: '' }),
    [],
  );
  const openCreateSubtask = useCallback((parentId) => setCreating({ columnId: '', parentId }), []);
  const closeCreateTask = useCallback(() => setCreating(null), []);

  /**
   * Le `POST` vit ici, pas dans `TaskForm` : le formulaire reste présentationnel (même
   * contrat que `ProjectForm`) et c'est la page qui sait rafraîchir toutes les vues.
   *
   * L'erreur est **relancée** après l'alerte pour que `TaskForm` l'affiche et garde la
   * saisie : un refus courant — projet archivé, colonne d'un autre projet — ne doit pas
   * faire perdre ce qui a été tapé.
   */
  const createTask = useCallback(
    async (payload) => {
      setSubmitting(true);
      try {
        await api.post(`/projects/${projectId}/tasks`, payload);
        setCreating(null);
        setReloadKey((k) => k + 1);
      } catch (error) {
        await appAlert(t('project.createTaskFailed', { message: error.message }), {
          title: t('project.newTask'),
        });
        throw error;
      } finally {
        setSubmitting(false);
      }
    },
    [projectId, t],
  );

  const period = useMemo(() => {
    if (!project?.startDate && !project?.endDate) return null;
    return `${project?.startDate ?? '…'} → ${project?.endDate ?? '…'}`;
  }, [project]);

  if (projectError) {
    // 404 = projet inconnu ou inaccessible (le serveur ne distingue pas, pas de fuite
    // d'existence). Toute autre erreur est un échec de chargement : on le dit, on propose de
    // réessayer — « introuvable » serait faux sur une panne réseau ou un 500.
    if (projectError.status !== 404) {
      return (
        <div className="p-6">
          <LoadError error={projectError} onRetry={reloadProject} />
        </div>
      );
    }
    return (
      <div className="p-6">
        <Eyebrow>{t('project.notFoundTitle')}</Eyebrow>
        <p className="mt-2 text-sm text-ink-soft">{t('project.notFound')}</p>
      </div>
    );
  }

  /*
   * Le serveur reste l'autorité : `POST /tasks` et `POST /tasks/:id/move` exigent le rôle
   * `member` et refusent un projet archivé. On masque la création et on retire l'affordance
   * de déplacement dans les mêmes conditions — pas comme protection, mais pour ne pas
   * proposer une action dont on sait qu'elle rendra une erreur.
   */
  const canEditTasks =
    Boolean(project) && roleAtLeast(project.role, 'member') && project.status !== 'archived';

  // Le partage (invitations, droits granulaires) exige au moins le rôle d'administrateur du
  // projet — même seuil que `POST /projects/:id/invitations` et `PATCH .../members/:userId`
  // côté serveur : masquer le bouton dans les mêmes conditions évite un 403 prévisible.
  // Aucune invitation dans l'espace personnel (ORG-2) : le bouton « Partager » n'ouvre que le
  // dialogue membres/invitations, qui n'a pas de sens sans organisation.
  const isPersonal = project?.organizationKind === 'personnel';
  const canShare = Boolean(project) && !isPersonal && roleAtLeast(project.role, 'admin');

  // `projectKey` descend jusqu'aux vues : la référence lisible d'une tâche est `CLE-numéro`,
  // et l'API des tâches ne renvoie que le numéro — la clé n'existe qu'au niveau du projet.
  const sharedProps = { projectId, projectKey: project?.key, onOpenTask: openTask, reloadKey };

  return (
    <div className="flex flex-col">
      {/* Pleine largeur (`bounded={false}`) : l'espace de travail d'un projet ne se borne pas
          à `--largeur-corps` (cf. `PageBody`), son en-tête non plus. */}
      <PageHeader
        bounded={false}
        eyebrow={project?.key ?? '…'}
        title={project?.name ?? t('project.loadingName')}
        meta={
          (project?.status || period) && (
            <>
              {project?.status && <StatusTag status={project.status} />}
              {period && <span className="font-mono text-[11px] text-ink-faint">{period}</span>}
            </>
          )
        }
        actions={
          <>
            {stats && (
              <div className="mr-2 w-48">
                {/* Avancement pondéré par la charge, pas la moyenne des pourcentages :
                    une tâche de 60 h à 50 % ne pèse pas comme une tâche de 2 h à 50 %. */}
                <Progress
                  value={stats.load?.weightedProgress ?? 0}
                  label={t('project.globalProgress')}
                />
              </div>
            )}
            {canShare && (
              <button type="button" className={BTN_GHOST} onClick={() => setSharingOpen(true)}>
                {t('project.share')}
              </button>
            )}
          </>
        }
        primary={
          canEditTasks && (
            <PrimaryButton onClick={() => openCreateTask('')}>{t('project.newTask')}</PrimaryButton>
          )
        }
      />

      <nav
        className="flex items-end gap-1 border-b-2 border-line px-4 pt-2"
        aria-label={t('project.viewsAriaLabel')}
      >
        {onglettesCompacts ? (
          <>
            {TABS.filter((tab) => tab.key === activeTab).map((tab) => (
              <button key={tab.key} type="button" className={TAB_ACTIVE} aria-current="page">
                {tab.label}
              </button>
            ))}
            <Deroulant
              label={t('project.moreViewsAriaLabel')}
              declencheur={({ ouvert }) => (
                <span className={`${TAB_IDLE} inline-flex items-center gap-1.5`}>
                  {t('project.moreViews')}
                  <Chevron ouvert={ouvert} />
                </span>
              )}
            >
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={activeTab === tab.key}
                  className={MENU_RADIO}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  <span
                    className={activeTab === tab.key ? MENU_CHECK_ON : MENU_CHECK_OFF}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </Deroulant>
          </>
        ) : (
          TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={activeTab === tab.key ? TAB_ACTIVE : TAB_IDLE}
              aria-current={activeTab === tab.key ? 'page' : undefined}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))
        )}
      </nav>

      <div className="flex-1 p-4">
        {activeTab === 'tableau-de-bord' && <DashboardView {...sharedProps} />}
        {activeTab === 'kanban' && (
          <KanbanView
            {...sharedProps}
            canMove={canEditTasks}
            onCreateTask={canEditTasks ? openCreateTask : null}
          />
        )}
        {activeTab === 'planning' && (
          <PlanningView {...sharedProps} onChanged={onTaskChanged} canCreate={canEditTasks} />
        )}
        {activeTab === 'gantt' && <GanttView {...sharedProps} />}
        {activeTab === 'liste' && <ListView {...sharedProps} />}
        {activeTab === 'transfert' && (
          <TransferPanel projectId={projectId} projectKey={project?.key} />
        )}
        {activeTab === 'reglages' && (
          <SettingsView
            projectId={projectId}
            projectKey={project?.key}
            projectName={project?.name}
            organizationKind={project?.organizationKind}
            projectStatus={project?.status}
            // Le serveur exige les deux : rôle `admin` ET droit de suppression.
            canDelete={Boolean(project?.canDelete) && roleAtLeast(project?.role, 'admin')}
            onTransferred={reloadProject}
            onDeleted={() => navigate('/projets', { replace: true })}
          />
        )}
      </div>

      {creating !== null && (
        <Modal title={t('project.newTask')} onClose={closeCreateTask}>
          <TaskForm
            projectId={projectId}
            defaultColumnId={creating.columnId}
            defaultParentId={creating.parentId}
            onSubmit={createTask}
            onCancel={closeCreateTask}
            submitting={submitting}
          />
        </Modal>
      )}

      {selectedTaskId && (
        <TaskDrawer
          projectId={projectId}
          projectKey={project?.key}
          taskId={selectedTaskId}
          canEdit={canEditTasks}
          onCreateSubtask={canEditTasks ? openCreateSubtask : null}
          onClose={closeTask}
          onChanged={onTaskChanged}
        />
      )}

      {sharingOpen && (
        <ShareProjectDialog
          projectId={projectId}
          canManage={canShare}
          onClose={() => setSharingOpen(false)}
        />
      )}
    </div>
  );
}
